/**
 * Pure H OS · Product Master · Knowledge Import workflow
 * =======================================================
 * 8-step user flow (all in one page):
 *   1. Create batch / pick existing draft batch
 *   2. Upload files (.pdf .docx .xlsx) with drag-drop, auto-detect kind
 *   3. Parse → shows matched code per file + fix-match dropdown
 *   4. Extract (Claude Sonnet 4.5) → structured field proposals
 *   5. Preview: Current Value ↓ Imported Value ↓ Source ↓ Confidence ↓ Approve
 *   6. Owner can edit any field inline (marks ownerEdited=true)
 *   7. Owner can force overwrite an already-populated field (checkbox)
 *   8. Apply selected fields — server writes with full provenance + guards
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, FileText, FileSpreadsheet, FileCheck2, Sparkles, ChevronDown,
  ChevronRight, CheckCircle2, AlertTriangle, X, RefreshCcw, ArrowLeft,
  ClipboardCheck, Save, Trash2, Info, Plus,
} from "lucide-react";
import { Card, SectionHeader, Button, EmptyState } from "../../components/lib";
import { api, knowledgeImport, KIBatch, KIFile, KIProposal, productMaster } from "../../services/api";

const ORVAN_CODES = [
  "AEVUM","AUREL","AURYN","FELIN","FLOREL","GRAEL","KORV","LUMIO","LUMIS","MIREN",
  "OLVID","ORVYN","REVENE","SOLEN","SOLUM","SOREL","SYLVN","VAEHR","VELDT","VELORA",
];

// ── Helpers ──────────────────────────────────────────────────────
function humanBytes(n?: number) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
function fmt(iso?: string) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
async function fileToBase64(f: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}
function displayValue(v: any): string {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
function fileIcon(kind?: string) {
  if (kind === "xlsx") return FileSpreadsheet;
  if (kind === "docx") return FileText;
  return FileCheck2;
}

// ── Main component ──────────────────────────────────────────────
export default function KnowledgeImportPage() {
  const nav = useNavigate();
  const [ventures, setVentures] = useState<Array<{ id: string; name: string }>>([]);
  const [ventureId, setVentureId] = useState<string>("orvan");
  const [batches, setBatches] = useState<KIBatch[]>([]);
  const [batch, setBatch] = useState<KIBatch | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [selection, setSelection] = useState<Record<string, Set<string>>>({});
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const showFlash = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 3000); };

  // Boot
  useEffect(() => {
    (async () => {
      try {
        const vs = await api.ventures();
        const list = (vs as any[]).map((v) => ({ id: v.id, name: v.name || v.id }));
        setVentures(list);
        const orvan = list.find((v) => v.id === "orvan");
        setVentureId(orvan?.id || list[0]?.id || "orvan");
      } catch {
        setVentures([{ id: "orvan", name: "ORVAN Perfumes" }]);
      }
    })();
  }, []);

  const reloadBatches = useCallback(async () => {
    try { setBatches(await knowledgeImport.listBatches(ventureId)); } catch { setBatches([]); }
  }, [ventureId]);
  useEffect(() => { if (ventureId) reloadBatches(); }, [ventureId, reloadBatches]);

  const reloadBatch = useCallback(async () => {
    if (!batch) return;
    const fresh = await knowledgeImport.preview(ventureId, batch.id);
    setBatch(fresh);
  }, [ventureId, batch]);

  const openBatch = async (id: string) => {
    const b = await knowledgeImport.preview(ventureId, id);
    setBatch(b);
    // Preselect all proposals by default so operator only unchecks unwanted ones
    const sel: Record<string, Set<string>> = {};
    Object.entries(b.proposals || {}).forEach(([code, slot]) => {
      sel[code] = new Set(Object.keys(slot.fields || {}));
    });
    setSelection(sel);
    setExpandedProducts(new Set(Object.keys(b.proposals || {})));
  };

  const newBatch = async () => {
    setBusy("new"); setErr(null);
    try {
      const b = await knowledgeImport.createBatch(ventureId);
      await reloadBatches();
      await openBatch(b.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to create batch");
    } finally { setBusy(null); }
  };

  const uploadFiles = async (files: FileList | File[]) => {
    if (!batch) return;
    setBusy("upload"); setErr(null);
    try {
      for (const f of Array.from(files)) {
        const b64 = await fileToBase64(f);
        await knowledgeImport.uploadFile(ventureId, batch.id, { name: f.name, mime: f.type || "", dataBase64: b64 });
      }
      await reloadBatch();
      showFlash(`${files.length} file(s) uploaded.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally { setBusy(null); }
  };

  const parse = async () => {
    if (!batch) return;
    setBusy("parse"); setErr(null);
    try {
      await knowledgeImport.parse(ventureId, batch.id);
      await reloadBatch();
      showFlash("Files parsed. Review matches then click Extract.");
    } catch (e) { setErr(e instanceof Error ? e.message : "Parse failed"); }
    finally { setBusy(null); }
  };

  const extract = async () => {
    if (!batch) return;
    setBusy("extract"); setErr(null);
    try {
      const fresh = await knowledgeImport.extract(ventureId, batch.id);
      setBatch(fresh);
      const sel: Record<string, Set<string>> = {};
      Object.entries(fresh.proposals || {}).forEach(([code, slot]) => {
        sel[code] = new Set(Object.keys(slot.fields || {}));
      });
      setSelection(sel);
      setExpandedProducts(new Set(Object.keys(fresh.proposals || {})));
      showFlash("AI extraction complete. Review each field, then Apply.");
    } catch (e) { setErr(e instanceof Error ? e.message : "Extract failed"); }
    finally { setBusy(null); }
  };

  const applyBatch = async () => {
    if (!batch) return;
    const selections: Record<string, string[]> = {};
    Object.entries(selection).forEach(([code, set]) => {
      if (set.size > 0) selections[code] = Array.from(set);
    });
    if (Object.keys(selections).length === 0) {
      setErr("Nothing selected. Check at least one field before applying.");
      return;
    }
    setBusy("apply"); setErr(null);
    try {
      const res = await knowledgeImport.apply(ventureId, batch.id, selections);
      const written = res.results.reduce((n, r) => n + (r.written?.length || 0), 0);
      const skipped = res.results.reduce((n, r) => n + (r.skipped?.length || 0), 0);
      showFlash(`Applied · ${written} fields written · ${skipped} skipped.`);
      await reloadBatch();
      await reloadBatches();
    } catch (e) { setErr(e instanceof Error ? e.message : "Apply failed"); }
    finally { setBusy(null); }
  };

  const discardBatch = async () => {
    if (!batch) return;
    if (!confirm("Discard this batch? Uploaded files will be removed.")) return;
    await knowledgeImport.deleteBatch(ventureId, batch.id);
    setBatch(null); setSelection({});
    await reloadBatches();
    showFlash("Batch discarded.");
  };

  const changeMatch = async (fileId: string, code: string | null) => {
    if (!batch) return;
    await knowledgeImport.editFileMatch(ventureId, batch.id, fileId, code);
    await reloadBatch();
  };

  const editField = async (code: string, field: string, value: any) => {
    if (!batch) return;
    await knowledgeImport.editProposal(ventureId, batch.id, code, { field, value });
    await reloadBatch();
  };

  const setOverwrite = async (code: string, field: string, overwrite: boolean) => {
    if (!batch) return;
    await knowledgeImport.editProposal(ventureId, batch.id, code, { field, overwrite });
    await reloadBatch();
  };

  const toggleFieldSelected = (code: string, field: string, on: boolean) => {
    setSelection((s) => {
      const set = new Set(s[code] || []);
      if (on) set.add(field); else set.delete(field);
      return { ...s, [code]: set };
    });
  };

  const toggleProductSelectAll = (code: string, on: boolean) => {
    setSelection((s) => {
      const slot = batch?.proposals?.[code];
      const all = new Set(on ? Object.keys(slot?.fields || {}) : []);
      return { ...s, [code]: all };
    });
  };

  const toggleExpanded = (code: string) => {
    setExpandedProducts((s) => {
      const n = new Set(s);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div data-testid="knowledge-import-root">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <button onClick={() => nav("/pureh/product-master")}
            className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1 hover:text-slate-800"
            data-testid="back-to-pm">
            <ArrowLeft size={12} /> Back to Product Master
          </button>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "var(--ph-display-font)" }} data-testid="page-title">
            Knowledge Import
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            Populate the 20 ORVAN products from the Product Bible + Home Blending Guide. Preview → Edit → Confirm → Save.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Venture</label>
          <select
            value={ventureId}
            onChange={(e) => { setVentureId(e.target.value); setBatch(null); }}
            className="px-3 py-2 text-sm border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            data-testid="venture-select"
          >
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {!batch && (
            <Button variant="primary" onClick={newBatch} disabled={busy === "new"} testId="new-batch-btn">
              <Plus size={14} /> {busy === "new" ? "Creating…" : "New import batch"}
            </Button>
          )}
        </div>
      </div>

      {err && (
        <Card className="mb-4" testId="ki-error">
          <div className="flex items-start gap-2 text-red-600">
            <AlertTriangle size={16} className="mt-0.5" />
            <div>
              <div className="text-sm font-semibold">Error</div>
              <div className="text-xs">{err}</div>
            </div>
            <button className="ml-auto text-slate-400 hover:text-slate-800" onClick={() => setErr(null)} data-testid="dismiss-error">
              <X size={14} />
            </button>
          </div>
        </Card>
      )}

      {!batch ? (
        <BatchesList batches={batches} onOpen={openBatch} />
      ) : (
        <>
          <WorkflowStrip batch={batch} busy={busy}
            onParse={parse} onExtract={extract} onApply={applyBatch}
            onDiscard={discardBatch} onReload={reloadBatch}
            selectionCount={Object.values(selection).reduce((n, s) => n + s.size, 0)}
          />
          <FilesCard batch={batch} onUpload={uploadFiles} onChangeMatch={changeMatch} busy={busy} />
          <ProposalsCard
            batch={batch}
            selection={selection}
            expanded={expandedProducts}
            onToggleExpand={toggleExpanded}
            onToggleField={toggleFieldSelected}
            onToggleProductAll={toggleProductSelectAll}
            onEditField={editField}
            onToggleOverwrite={setOverwrite}
          />
          {batch.applyResults && batch.applyResults.length > 0 && (
            <ApplyResultsCard results={batch.applyResults} />
          )}
        </>
      )}

      {flash && (
        <div className="fixed bottom-6 right-6 px-4 py-2 bg-slate-900 text-white text-sm rounded shadow-lg z-50" data-testid="flash-toast">
          {flash}
        </div>
      )}
    </div>
  );
}

// ── Batches list ────────────────────────────────────────────────
function BatchesList({ batches, onOpen }: { batches: KIBatch[]; onOpen: (id: string) => void }) {
  if (batches.length === 0) {
    return (
      <Card testId="empty-batches">
        <EmptyState title="No import batches yet" hint="Click 'New import batch' to start." />
      </Card>
    );
  }
  return (
    <Card testId="batches-list">
      <SectionHeader eyebrow="Import batches" title="Recent" />
      <ul className="space-y-2">
        {batches.map((b) => (
          <li key={b.id}>
            <button onClick={() => onOpen(b.id)}
              className="w-full flex items-center justify-between p-3 rounded border hover:bg-slate-50 text-left"
              style={{ borderColor: "var(--ph-border-subtle)" }}
              data-testid={`batch-row-${b.id}`}>
              <div>
                <div className="text-sm font-semibold text-slate-900">{b.id}</div>
                <div className="text-xs text-slate-500">{fmt(b.createdAt)} · {b.creatorEmail || "system"}</div>
              </div>
              <span
                className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wider"
                style={{
                  background: b.status === "applied" ? "var(--ph-success-tint)"
                    : b.status === "discarded" ? "var(--ph-danger-tint)"
                    : "var(--ph-warning-tint)",
                  color: b.status === "applied" ? "var(--ph-success)"
                    : b.status === "discarded" ? "var(--ph-danger)"
                    : "var(--ph-warning)",
                }}
              >{b.status}</span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// Step indicator used by WorkflowStrip
function StepChip({ n, active, done, label }: { n: number; active: boolean; done: boolean; label: string }) {
  return (
    <span className="flex items-center gap-2 text-xs" data-testid={`step-${n}`}>
      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
        style={{
          background: done ? "var(--ph-success)" : active ? "var(--ph-brand-primary)" : "var(--ph-neutral-tint)",
          color: done || active ? "#fff" : "var(--ph-ink-muted)",
        }}>
        {done ? "✓" : n}
      </span>
      <span style={{ color: active ? "var(--ph-ink-strong)" : "var(--ph-ink-muted)" }}>{label}</span>
    </span>
  );
}

// ── Workflow strip (steps + actions) ────────────────────────────
function WorkflowStrip({ batch, busy, selectionCount, onParse, onExtract, onApply, onDiscard, onReload }: {
  batch: KIBatch; busy: string | null; selectionCount: number;
  onParse: () => void; onExtract: () => void; onApply: () => void;
  onDiscard: () => void; onReload: () => void;
}) {
  const stepStates = useMemo(() => {
    const has = batch.files.length > 0;
    const parsed = batch.files.some((f) => f.parsed);
    const extracted = !!batch.extractedAt;
    const applied = batch.status === "applied";
    return { has, parsed, extracted, applied };
  }, [batch]);

  return (
    <Card className="mb-4" testId="workflow-strip">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <StepChip n={1} active={!stepStates.has}                       done={stepStates.has}       label="Upload" />
          <StepChip n={2} active={stepStates.has  && !stepStates.parsed}   done={stepStates.parsed}    label="Parse" />
          <StepChip n={3} active={stepStates.parsed && !stepStates.extracted} done={stepStates.extracted} label="AI Extract" />
          <StepChip n={4} active={stepStates.extracted && !stepStates.applied} done={stepStates.applied}   label="Confirm & Save" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onReload} testId="reload-batch"><RefreshCcw size={13} /> Reload</Button>
          <Button variant="secondary" onClick={onParse}  disabled={!stepStates.has || busy === "parse"} testId="parse-btn">
            <FileCheck2 size={14} /> {busy === "parse" ? "Parsing…" : "Parse files"}
          </Button>
          <Button variant="secondary" onClick={onExtract} disabled={!stepStates.parsed || busy === "extract"} testId="extract-btn">
            <Sparkles size={14} /> {busy === "extract" ? "Extracting (AI)…" : "AI Extract"}
          </Button>
          <Button variant="primary" onClick={onApply} disabled={!stepStates.extracted || busy === "apply" || selectionCount === 0} testId="apply-btn">
            <Save size={14} /> {busy === "apply" ? "Applying…" : `Apply ${selectionCount} field${selectionCount === 1 ? "" : "s"}`}
          </Button>
          <Button variant="ghost" onClick={onDiscard} testId="discard-btn"><Trash2 size={14} /></Button>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-3">
        Batch <span className="font-semibold">{batch.id}</span> · Status:{" "}
        <span className="font-semibold" data-testid="batch-status">{batch.status}</span>
      </div>
    </Card>
  );
}

// ── Files card (upload + match fix) ────────────────────────────
function FilesCard({ batch, onUpload, onChangeMatch, busy }: {
  batch: KIBatch;
  onUpload: (files: FileList | File[]) => void;
  onChangeMatch: (fileId: string, code: string | null) => void;
  busy: string | null;
}) {
  return (
    <Card className="mb-4" testId="files-card">
      <SectionHeader
        eyebrow="Source files"
        title={`${batch.files.length} file${batch.files.length === 1 ? "" : "s"} uploaded`}
        action={
          <label className="inline-flex items-center gap-1 cursor-pointer px-3 py-1.5 rounded text-xs font-semibold text-white"
            style={{ background: "var(--ph-brand-primary)" }}
            data-testid="upload-label">
            <Upload size={12} /> {busy === "upload" ? "Uploading…" : "Upload files"}
            <input type="file" multiple hidden
              accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={busy === "upload"}
              data-testid="upload-input"
              onChange={(e) => { const files = e.target.files; if (files && files.length) onUpload(files); e.target.value = ""; }} />
          </label>
        }
      />
      {batch.files.length === 0 ? (
        <EmptyState title="Drop the Product Bible + Home Blending Guide here" hint="Accepted formats: PDF · DOCX · XLSX (≤10 MB each)" />
      ) : (
        <div className="overflow-x-auto -mx-2 px-2">
          <table className="min-w-full text-sm" data-testid="files-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <th className="py-2 pr-3 font-semibold">File</th>
                <th className="py-2 pr-3 font-semibold">Kind</th>
                <th className="py-2 pr-3 font-semibold">Size</th>
                <th className="py-2 pr-3 font-semibold">Matched Product</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {batch.files.map((f) => {
                const Icon = fileIcon(f.kind);
                return (
                  <tr key={f.fileId} className="border-t" style={{ borderColor: "var(--ph-border-subtle)" }}
                    data-testid={`file-row-${f.fileId}`}>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className="text-slate-500 shrink-0" />
                        <div>
                          <div className="text-slate-800 font-medium">{f.name}</div>
                          {f.textPreview && <div className="text-[10px] text-slate-400 truncate max-w-md">{f.textPreview.slice(0, 90)}…</div>}
                          {f.parseError && <div className="text-[11px] text-red-600">Error: {f.parseError}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 uppercase text-xs">{f.kind}</td>
                    <td className="py-2 pr-3 text-slate-600">{humanBytes(f.sizeBytes)}</td>
                    <td className="py-2 pr-3">
                      {f.kind === "xlsx" ? (
                        <span className="text-[11px] text-slate-500">(multi — {f.recordCount || 0} rows)</span>
                      ) : (
                        <select
                          value={f.matchedCode || ""}
                          onChange={(e) => onChangeMatch(f.fileId, e.target.value || null)}
                          className="px-2 py-1 text-xs border rounded"
                          style={{ borderColor: "var(--ph-border-strong)" }}
                          data-testid={`match-select-${f.fileId}`}
                        >
                          <option value="">— unmatched —</option>
                          {ORVAN_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <MatchPill status={f.matchStatus || (f.parsed ? "unmatched" : "pending")} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {batch.extractionErrors && batch.extractionErrors.length > 0 && (
        <div className="mt-4 p-3 rounded border" style={{ borderColor: "var(--ph-danger)" , background: "var(--ph-danger-tint)" }} data-testid="extract-errors">
          <div className="text-[11px] uppercase tracking-[0.16em] text-red-700 font-semibold mb-1">Extraction errors</div>
          <ul className="text-xs text-red-700 space-y-0.5">
            {batch.extractionErrors.map((e, i) => <li key={i}>{e.fileId ? `${e.fileId}: ` : ""}{e.error}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}

function MatchPill({ status }: { status: string }) {
  const map: Record<string, [string, string, string]> = {
    matched:     ["Matched",     "var(--ph-success-tint)", "var(--ph-success)"],
    unmatched:   ["Unmatched",   "var(--ph-warning-tint)", "var(--ph-warning)"],
    error:       ["Error",       "var(--ph-danger-tint)",  "var(--ph-danger)"],
    excel_batch: ["Excel batch", "rgba(79,93,184,.10)",    "var(--ph-ai)"],
    pending:     ["Pending",     "var(--ph-neutral-tint)", "var(--ph-ink-muted)"],
  };
  const [label, bg, fg] = map[status] || map.pending;
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wider"
      style={{ background: bg, color: fg }}>{label}</span>
  );
}

// ── Proposals card (per-product diff + edit + approve) ──────────
function ProposalsCard({
  batch, selection, expanded, onToggleExpand, onToggleField, onToggleProductAll,
  onEditField, onToggleOverwrite,
}: {
  batch: KIBatch;
  selection: Record<string, Set<string>>;
  expanded: Set<string>;
  onToggleExpand: (code: string) => void;
  onToggleField: (code: string, field: string, on: boolean) => void;
  onToggleProductAll: (code: string, on: boolean) => void;
  onEditField: (code: string, field: string, value: any) => Promise<void>;
  onToggleOverwrite: (code: string, field: string, ow: boolean) => Promise<void>;
}) {
  const codes = Object.keys(batch.proposals || {}).sort();
  if (codes.length === 0) {
    return (
      <Card testId="empty-proposals">
        <SectionHeader eyebrow="Proposed knowledge" title="Nothing yet" />
        <EmptyState title="Nothing to preview" hint="Upload → Parse → AI Extract to see field-by-field proposals here." />
      </Card>
    );
  }
  return (
    <Card testId="proposals-card">
      <SectionHeader
        eyebrow="Preview · Edit · Confirm"
        title={`${codes.length} product${codes.length === 1 ? "" : "s"} to update`}
      />
      <div className="text-[11px] text-slate-500 mb-4 flex items-center gap-1">
        <Info size={12} /> Fill-empty-only is enforced by default. Tick <span className="font-semibold">Overwrite</span> to replace a populated field. Owner-approved fields are locked to AI.
      </div>
      <div className="space-y-3">
        {codes.map((code) => (
          <ProductRow
            key={code}
            code={code}
            slot={batch.proposals![code]}
            selected={selection[code] || new Set()}
            expanded={expanded.has(code)}
            onToggleExpand={() => onToggleExpand(code)}
            onToggleField={(f, on) => onToggleField(code, f, on)}
            onToggleAll={(on) => onToggleProductAll(code, on)}
            onEditField={(f, v) => onEditField(code, f, v)}
            onToggleOverwrite={(f, ow) => onToggleOverwrite(code, f, ow)}
          />
        ))}
      </div>
    </Card>
  );
}

function ProductRow({
  code, slot, selected, expanded, onToggleExpand, onToggleField, onToggleAll, onEditField, onToggleOverwrite,
}: {
  code: string;
  slot: KIBatch["proposals"][string];
  selected: Set<string>;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleField: (field: string, on: boolean) => void;
  onToggleAll: (on: boolean) => void;
  onEditField: (field: string, v: any) => Promise<void>;
  onToggleOverwrite: (field: string, ow: boolean) => Promise<void>;
}) {
  const fields = Object.keys(slot.fields || {}).sort();
  const total = fields.length;
  const selCount = selected.size;
  return (
    <div className="rounded border" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid={`product-row-${code}`}>
      <button onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50"
        data-testid={`toggle-${code}`}>
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <div className="text-left">
            <div className="text-base font-semibold text-slate-900" style={{ fontFamily: "var(--ph-display-font)" }}>{code}</div>
            <div className="text-[11px] text-slate-500">{selCount}/{total} field(s) selected</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={selCount === total && total > 0}
              onChange={(e) => onToggleAll(e.target.checked)}
              data-testid={`select-all-${code}`} />
            <span>Select all</span>
          </label>
        </div>
      </button>
      {expanded && (
        <div className="border-t p-3 space-y-3" style={{ borderColor: "var(--ph-border-subtle)" }}>
          {fields.map((f) => (
            <FieldRow
              key={f}
              code={code}
              field={f}
              proposal={slot.fields[f]}
              currentValue={slot.currentValues?.[f]}
              selected={selected.has(f)}
              onToggle={(on) => onToggleField(f, on)}
              onEdit={(v) => onEditField(f, v)}
              onToggleOverwrite={(ow) => onToggleOverwrite(f, ow)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FieldRow({ code, field, proposal, currentValue, selected, onToggle, onEdit, onToggleOverwrite }: {
  code: string; field: string; proposal: KIProposal; currentValue: any;
  selected: boolean;
  onToggle: (on: boolean) => void;
  onEdit: (v: any) => Promise<void>;
  onToggleOverwrite: (ow: boolean) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() =>
    Array.isArray(proposal.value) ? proposal.value.join(", ") : String(proposal.value ?? ""),
  );
  const isPopulated = currentValue !== null && currentValue !== undefined && currentValue !== "" &&
                       !(Array.isArray(currentValue) && currentValue.length === 0);
  const requiresOverwrite = isPopulated && !proposal.overwrite;

  const conf = proposal.confidence ?? 0;
  const confTone = conf >= 90 ? "success" : conf >= 60 ? "warning" : "danger";
  const confBg = confTone === "success" ? "var(--ph-success-tint)" : confTone === "warning" ? "var(--ph-warning-tint)" : "var(--ph-danger-tint)";
  const confFg = confTone === "success" ? "var(--ph-success)"      : confTone === "warning" ? "var(--ph-warning)"     : "var(--ph-danger)";

  const commit = async () => {
    let v: any = draft;
    if (Array.isArray(proposal.value)) {
      v = draft.split(",").map((s) => s.trim()).filter(Boolean);
    } else if (typeof proposal.value === "number") {
      v = Number(draft);
      if (Number.isNaN(v)) v = null;
    }
    await onEdit(v);
    setEditing(false);
  };

  return (
    <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-start p-3 rounded"
      style={{ background: selected ? "var(--ph-warning-tint)" : "var(--ph-surface-alt)", border: "1px solid var(--ph-border-subtle)" }}
      data-testid={`field-row-${code}-${field}`}>
      <input type="checkbox" checked={selected} onChange={(e) => onToggle(e.target.checked)}
        className="mt-1"
        data-testid={`select-${code}-${field}`} />

      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-2">
          <span>{field}</span>
          {proposal.aiGenerated && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-semibold rounded"
              style={{ background: "rgba(79,93,184,.10)", color: "var(--ph-ai)" }}>
              <Sparkles size={9} /> AI GENERATED
            </span>
          )}
          {proposal.ownerEdited && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-semibold rounded"
              style={{ background: "var(--ph-success-tint)", color: "var(--ph-success)" }}>
              <CheckCircle2 size={9} /> OWNER APPROVED
            </span>
          )}
          {proposal.language && <span className="text-[9px] text-slate-400">{proposal.language.toUpperCase()}</span>}
        </div>

        {/* Current ↓ Imported */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          <div>
            <div className="text-[9px] font-bold text-slate-500 tracking-wider">CURRENT VALUE</div>
            <div className="text-xs text-slate-700 mt-0.5 min-h-[24px] p-1.5 rounded"
              style={{ background: "var(--ph-neutral-tint)" }}
              data-testid={`current-${code}-${field}`}>{displayValue(currentValue)}</div>
          </div>
          <div>
            <div className="text-[9px] font-bold text-emerald-700 tracking-wider">IMPORTED VALUE</div>
            {editing ? (
              <div className="flex gap-1 mt-0.5">
                <textarea rows={Array.isArray(proposal.value) || String(proposal.value ?? "").length > 40 ? 2 : 1}
                  value={draft} onChange={(e) => setDraft(e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border rounded"
                  style={{ borderColor: "var(--ph-border-strong)" }}
                  data-testid={`edit-input-${code}-${field}`} />
                <Button variant="primary" onClick={commit} testId={`edit-save-${code}-${field}`}>
                  <Save size={12} />
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)} testId={`edit-cancel-${code}-${field}`}>
                  <X size={12} />
                </Button>
              </div>
            ) : (
              <div className="flex items-start gap-1">
                <div className="flex-1 text-xs text-slate-700 mt-0.5 min-h-[24px] p-1.5 rounded"
                  style={{ background: "var(--ph-success-tint)" }}
                  data-testid={`imported-${code}-${field}`}>{displayValue(proposal.value)}</div>
                <button onClick={() => setEditing(true)} className="text-[10px] text-blue-600 underline mt-1"
                  data-testid={`edit-btn-${code}-${field}`}>
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Meta strip */}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold"
            style={{ background: confBg, color: confFg }}
            data-testid={`confidence-${code}-${field}`}>
            {conf}% confidence
          </span>
          <span data-testid={`source-${code}-${field}`}>
            source: <span className="font-semibold">{proposal.sourceFileName || "—"}</span>
            {proposal.sourceKind === "xlsx" && proposal.sourceSheet ? ` · sheet ${proposal.sourceSheet}` : ""}
            {proposal.sourcePage && proposal.sourceKind !== "xlsx" ? ` · pg ${proposal.sourcePage}` : ""}
          </span>
          {proposal.sourceSnippet && (
            <span className="italic text-slate-400 truncate max-w-md">&ldquo;{proposal.sourceSnippet.slice(0, 90)}&rdquo;</span>
          )}
        </div>
      </div>

      {/* Overwrite toggle (only if current is populated) */}
      <div className="pt-1">
        {isPopulated && (
          <label className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 cursor-pointer"
            data-testid={`overwrite-${code}-${field}`}>
            <input type="checkbox" checked={!!proposal.overwrite}
              onChange={(e) => onToggleOverwrite(e.target.checked)} />
            <span>Overwrite</span>
          </label>
        )}
        {requiresOverwrite && (
          <div className="text-[9px] text-amber-600 mt-1 max-w-[80px] text-right">will be skipped</div>
        )}
      </div>
    </div>
  );
}

function ApplyResultsCard({ results }: { results: Array<{ code: string; written?: string[]; skipped?: any[]; error?: string }> }) {
  return (
    <Card className="mt-4" testId="apply-results">
      <SectionHeader eyebrow="Apply results" title="What was saved" />
      <ul className="space-y-2">
        {results.map((r, i) => (
          <li key={i} className="p-3 rounded border" style={{ borderColor: "var(--ph-border-subtle)" }}
            data-testid={`result-${r.code}`}>
            <div className="text-sm font-semibold text-slate-900">{r.code}</div>
            {r.error && <div className="text-xs text-red-600">Error: {r.error}</div>}
            {r.written && r.written.length > 0 && (
              <div className="text-xs text-emerald-700 mt-1">
                Written ({r.written.length}): {r.written.join(", ")}
              </div>
            )}
            {r.skipped && r.skipped.length > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                Skipped ({r.skipped.length}): {r.skipped.map((s: any) => `${s.path} (${s.reason})`).join(" · ")}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
